/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { formatLabelWithParentheses, withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { renderTags } from "kontrolEdge/helpers/renderTags";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../Shared/mapKeListResourceTags";
import {
    resolveServiceStatusTag,
    serviceStatusRank,
} from "kontrolEdge/helpers/serviceStatusTag";

export interface Cloud {
	id: number;
	customer?: {
		name: string;
		id: number;
		chargebeeCustomerId: string
	};
	kontrolEdgePort:{
		datacenter: {
			name: string;
		}
		portType?: string;
	}
	subscriptionId: string;
	cloud: string;
	cloudLocation: string;
	connectionName: string;
	mbps: string;
	mbpsNum: number;
	vlan: string;
	parentConnection: string;
	parentConnectionId: string;
	tagsRich?: ResourceTagSummary[];
	name: string;
	provider: string;
	connectionCloudId?: string | null; // e.g. "dxcon-fgpntsii"
	status: string;
  }

  function toNumberOrZero(x: unknown): number {
	if (typeof x === "number") return Number.isFinite(x) ? x : 0;
	const n = Number(String(x ?? "").replace(/,/g, ""));
	return Number.isFinite(n) ? n : 0;
  }

  const formatProviderName = (provider: string | undefined | null): string => {
    if (!provider) return "-";
  
    const map: Record<string, string> = {
      consoleConnect: "Console Connect",
      equinix: "Equinix",
      megaport: "Megaport",
    };
  
    return map[provider] || provider.charAt(0).toUpperCase() + provider.slice(1);
  };


export const cloudColumns: GenericTableColumn<Cloud>[] = [
	{
		header: "Connection ID",
		accessor: "subscriptionId",
		renderCell: (row) => (
			<GenericButton variant="brand" label={row.subscriptionId} />
		),
	},
	{
		header: "Parent Connection",
		accessor: "parentConnection",
	},
	{
		header: "Name",
		accessor: "name",
		renderCell: (row) => <GenericTag variant="neutral" label={row.name} size="small"/>,
	},
	{
		header: "Provider",
		accessor: "provider",
		renderCell: (row) => formatProviderName(row.provider),
	},
	{
		header: "Origin",
		renderCell: (row) => row.kontrolEdgePort?.datacenter?.name ?? "-",
	},
	{
		header: "Connection Status",
		renderCell: (row) => {
		const status = row.status;
		const { label, variant } = resolveServiceStatusTag(status);
		return <GenericTag variant={variant} label={label} size="small" />;
		},
		sortAccessor: (row) => serviceStatusRank(row.status),
	},
	{
		header: "Tag",
		renderCell: (row) => (
			<ResourceTagsTableCell
				tagsRich={row.tagsRich}
				maxVisible={1}
				triggerSingleRow
			/>
		),
	},
	{
		header: "Mbps",
		accessor: "mbps",
	},
	{
		header: "VLAN",
		accessor: "vlan",
	},
	{
		header: "Cloud (Account)",
		accessor: "cloud",
	},
	{
		header: "Cloud Conn. ID",
		renderCell: (row) => row.connectionCloudId ?? "—",
	},
];

export const shapeCloudData = (connections: any[]): Cloud[] => {

return connections.map((c) => {
	// Build parentConnection
	// A VXC terminating on a BBaaS belongs to the backbone; the access port
	// only carries it, so the backbone wins as the parent connection.
	const parentConnection = c.backbone?.subscriptionId
	? `[BACKBONE]${c.backbone.subscriptionId}`
	: c.kontrolEdgePort?.subid
	? `[PORT]${c.kontrolEdgePort.subid}`
	: c.kontrolEdgeLag?.subid
	? `[LAG]${c.kontrolEdgeLag.subid}`
	: "-";

	const parentConnectionId =
	c.backbone?.id ?? c.kontrolEdgePort?.id ?? c.kontrolEdgeLag?.id ?? "-";
	const cloud = formatLabelWithParentheses(c.cloud, c.account);

	const customer = c.customer
	? {
		name: c.customer.name ?? "-",
		id: c.customer.id ?? "-",
		chargebeeCustomerId: c.customer.chargebeeCustomerId ?? "-"
		}
	: undefined;
	const mbpsNum = toNumberOrZero(c.mbps);
	const tagsRich = mapKeListResourceTags(c);

	return {
	id: c.id ?? 0,
	customer,
	subscriptionId: withFallback(c.subscriptionId),
	cloud,
	provider: c.provider ?? '-',
	cloudLocation: withFallback(c.kontrolEdgePort?.datacenter?.displayName),
	connectionName: withFallback(c.name),
	mbps: withFallback(c.mbps),
	mbpsNum,
	vlan: withFallback(c.vlan),
	parentConnection,
	parentConnectionId: withFallback(parentConnectionId),
	tagsRich,
	name: withFallback(c.name),
	status: withFallback(c.status),
	kontrolEdgePort: {
        datacenter: {
          name: c?.kontrolEdgePort?.datacenter?.name ?? "-",
        },
      },
	connectionCloudId: c.connectionCloudId
	};
	});
  };